import * as m from "../model/model";

import { CoursesResponse, ExamsQuery, ExamsResponse } from "./api-model";

import { AudioData } from "../pages/app/lessons/Lessons.service";
import { User } from "../model/model";
import axios from "axios";

const axi = axios.create({
  baseURL: "", //"http://localhost:3001", // Backend URL
  withCredentials: true,
});

export const setAuthToken = (token: string | null) => {
  if (token) {
    axi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axi.defaults.headers.common["Authorization"];
  }
};

const handleError = (error: any) => {
  console.error("API error response:", { error });
  if (error.response) {
    if (error.response.status === 401) {
      // Unauthorized
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?redirect=" + window.location.pathname;
      }
    } else {
      const err = error.response.data;
      if (err) {
        throw new Error(`${error.response.statusText} ${err}`);
      } else {
        throw new Error(error);
      }
    }
  }
};

export const api = {
  login: {
    sendPasswordResetEmail: async (email: string) => {
      try {
        const response = await axi.get(
          `/login/send-password-reset-email/${email}`
        );
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    sessionLogin: async (payload: {
      email: string;
      idToken: string;
      csrfToken: string;
    }) => {
      try {
        // no /app prefix since this is a public route
        const response = await axi.post<User>("/login/session", payload);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    logOutUser: async () => {
      try {
        const response = await axi.post("/login/logout");
        setAuthToken(null);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    getCustomToken: async (idToken: string) => {
      try {
        const response = await axi.post<{ token: string }>(
          "/login/custom-token",
          { idToken }
        );
        return response.data.token;
      } catch (error) {
        handleError(error);
      }
    },
  },

  app: {
    getUser: async () => {
      try {
        const response = await axi.get("/app/user");
        return response.data;
      } catch (error) {
        console.error("Failed to fetch user:", error);
        handleError(error);
      }
    },
    updateUser: async (user: User) => {
      // rename to progress
      try {
        const body = {
          email: user.email,
          previous: user.previousLessonUID,
          progress: user.progressLessonUID,
        };
        const response = await axi.post("/app/user", body);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },

    audioCompleted: async (audio: AudioData): Promise<User> => {
      return axi
        .put("/app/audio-completed", audio)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to update audio completion:", error);
          handleError(error);
        });
    },

    updateLessonMaterial: async (
      materialId: string,
      page: number,
      scale: number
    ) => {
      try {
        const response = await axi.post("/app/lesson-material", {
          id: materialId,
          page,
          scale,
        });
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    setCurrentCourse: async (email: string, course: string) => {
      try {
        const response = await axi.post("/app/set-current-course", {
          email,
          course,
        });
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    getOverviewCourses: async () => {
      try {
        const response = await axi.get("/app/overview-courses");
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
  },

  exam: {
    create: async (email: string) => {
      try {
        const response = await axi.post("/prof/create", { email });
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    getExam: async (id: string) => {
      try {
        const response = await axi.get<{
          status: string;
          html: string;
          id: string;
          email: string;
        }>(`/prof/${id}`);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    save: async (id: string, answers: Record<string, FormDataEntryValue>) => {
      try {
        const response = await axi.post(`/prof/${id}/save`, answers);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
    submit: async (id: string, answers: Record<string, FormDataEntryValue>) => {
      try {
        const response = await axi.post(`/prof/${id}/submit`, answers);
        return response.data;
      } catch (error) {
        handleError(error);
      }
    },
  },

  admin: {
    user: {
      getUsers: async () => {
        try {
          const response = await axi.get<m.FbUser[]>("/admin/users");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getUser: async (uid: string) => {
        try {
          const response = await axi.get<m.User>(`/admin/user/${uid}`);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      updateUser: async (user: m.User) => {
        try {
          const response = await axi.put("/admin/user", user);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      createUser: async (name: string, email: string) => {
        try {
          const response = await axi.post("/admin/user", { name, email });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteUser: async (uid: string) => {
        try {
          const response = await axi.delete("/admin/user", { data: { uid } });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    courses: {
      getCourses: async () => {
        try {
          const response = await axi.get<CoursesResponse>("/admin/courses");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getCourse: async (token: string) => {
        try {
          const response = await axi.get(`/admin/course/${token}`);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getCourseByEmail: async (email: string) => {
        try {
          const response = await axi.get(`/admin/course-email/${email}`);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      createCourse: async (course: any) => {
        try {
          const response = await axi.post("/admin/course", course);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      updateCourse: async (course: any) => {
        try {
          const response = await axi.put("/admin/course", course);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteCourse: async (uid: string) => {
        try {
          const response = await axi.delete("/admin/course", { data: { uid } });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    lessons: {
      getLessons: async (courseId: string) => {
        try {
          const response = await axi.get(`/admin/lessons/${courseId}`);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      createLesson: async (lesson: any) => {
        try {
          const response = await axi.post("/admin/lesson", lesson);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      updateLesson: async (lesson: any) => {
        try {
          const response = await axi.put("/admin/lesson", lesson);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteLesson: async (uid: string) => {
        try {
          const response = await axi.delete("/admin/lesson", { data: { uid } });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getTutorings: async () => {
        try {
          const response = await axi.get("/admin/tutorings");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      createTutoring: async (tutoring: any) => {
        try {
          const response = await axi.put("/admin/tutoring", tutoring);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      updateTutoring: async (tutoring: any) => {
        try {
          const response = await axi.post("/admin/tutoring", tutoring);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteTutoring: async (uid: string) => {
        try {
          const response = await axi.delete("/admin/tutoring", {
            data: { uid },
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    logs: {
      getLogs: async () => {
        try {
          const response = await axi.get("/admin/logs");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    bucket: {
      listFiles: async (prefix: string) => {
        try {
          const response = await axi.get<m.FilesResponse>(
            "/admin/bucket/list?prefix=" + prefix
          );
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      uploadFile: async (file: File, path: string) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("path", path);
          const response = await axi.post("/admin/bucket/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteFile: async (filename: string) => {
        try {
          const response = await axi.post("/admin/bucket/delete", { filename });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      downloadFile: async (filename: string) => {
        try {
          const response = await axi.post("/admin/bucket/download", {
            filename,
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      /** file names are full paths */
      renameFile: async (oldName: string, newName: string) => {
        try {
          const response = await axi.post("/admin/bucket/rename", {
            oldName,
            newName,
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    workspaces: {
      createWorkspace: async (name: string) => {
        try {
          const response = await axi.post<m.Workspace>(
            "/admin/workspace/create",
            { name }
          );
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getWorkspaces: async () => {
        try {
          const response = await axi.get("/admin/workspace/list");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      updateWorkspace: async (workspace: m.Workspace) => {
        try {
          const response = await axi.post("/admin/workspace/update", {
            workspace,
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      deleteWorkspace: async (id: string) => {
        try {
          const response = await axi.delete("/admin/workspace/delete", {
            data: { id },
          });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    orders: {
      getOrders: async () => {
        try {
          const response = await axi.get<m.Order[]>("/admin/orders");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      getOrder: async (orderId: string) => {
        try {
          const response = await axi.get<m.Order>(`/admin/order/${orderId}`);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
    dump: {
      courses: async () => {
        return axi.post<string>("/dump/courses");
      },
      questions: async () => {
        return await axi.post("/dump/questions");
      },
      sections: async () => {
        return await axi.post("/dump/sections");
      },
    },
    exam: {
      list: async (query: ExamsQuery) => {
        try {
          const parms = new URLSearchParams(query).toString();
          const response = await axi.get<ExamsResponse>(
            "/admin/exam/list?" + parms
          );
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      get: async (id: string) => {
        try {
          const response = await axi.get<m.Exam>("/admin/exam/" + id);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      update: async (exam: Partial<m.Exam>) => {
        try {
          const response = await axi.patch("/admin/exam/" + exam.id, exam);
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      delete: async (id: string) => {
        try {
          const response = await axi.delete("/admin/exam", { data: { id } });
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      questions: async () => {
        try {
          const response = await axi.get("/admin/exam/questions");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      sections: async () => {
        try {
          const response = await axi.get("/admin/exam/sections");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
      airtable: async () => {
        try {
          const response = await axi.get("/admin/exam/airtable");
          return response.data;
        } catch (error) {
          handleError(error);
        }
      },
    },
  },
};
